import React, { useState, useMemo } from 'react';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  QrCodeIcon,
  InformationCircleIcon,
  PrinterIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import itraceGuides from '../data/documentation/itraceGuides';

// Matching categories get an icon. Extend this as more doc sets are added.
const CATEGORY_ICON = {
  Features: SparklesIcon,
  Setup: BookOpenIcon,
  Cycle: BookOpenIcon,
  iTrace: QrCodeIcon,
  myGAP: BookOpenIcon,
};

const Documentation = () => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // Flatten guides into a searchable list.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return itraceGuides;
    return itraceGuides.filter((g) => {
      const haystack = [
        g.title,
        g.summary,
        g.category,
        ...(g.tags || []),
        ...(g.sections || []).flatMap((s) => [s.heading, s.description, s.note, s.hook].filter(Boolean)),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const selected = selectedId ? itraceGuides.find((g) => g.id === selectedId) : null;

  // Group by category for the index view
  const grouped = useMemo(() => {
    const out = {};
    for (const g of results) {
      const cat = g.category || 'Other';
      out[cat] = out[cat] || [];
      out[cat].push(g);
    }
    return out;
  }, [results]);

  if (selected) {
    return (
      <div className="space-y-6 print-area">
        <div className="flex items-center justify-between print:hidden">
          <button
            onClick={() => setSelectedId(null)}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            data-testid="docs-back-btn"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to library
          </button>
          <button
            onClick={() => window.print()}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="docs-print-btn"
            title="Use your browser's Save as PDF option in the print dialog"
          >
            <PrinterIcon className="h-4 w-4" />
            Print / Save as PDF
          </button>
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-blue-600">
            {selected.category}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mt-1">{selected.title}</h1>
          <p className="text-gray-600 mt-2">{selected.summary}</p>
        </div>

        <div className="space-y-6">
          {selected.sections.map((section, idx) => (
            <div
              key={idx}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              data-testid={`docs-section-${idx}`}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">{section.heading}</h2>
              </div>
              <div className="px-6 py-4 space-y-3">
                {section.description && (
                  <p className="text-sm text-gray-800 leading-relaxed">{section.description}</p>
                )}
                {section.hook && (
                  <div className="flex items-start gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                    <InformationCircleIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="text-purple-900">
                      <strong>Where:</strong> <code className="bg-white px-1.5 py-0.5 rounded border border-purple-200 text-xs">{section.hook}</code>
                    </div>
                  </div>
                )}
                {section.note && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900">
                    <strong>Note:</strong> {section.note}
                  </div>
                )}
                {section.image && (
                  <figure className="mt-2">
                    <img
                      src={section.image}
                      alt={section.heading}
                      className="w-full border border-gray-200 rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                    <figcaption
                      style={{ display: 'none' }}
                      className="text-xs text-gray-500 mt-1 italic"
                    >
                      Screenshot placeholder — image not yet captured
                    </figcaption>
                  </figure>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BookOpenIcon className="h-8 w-8 text-blue-600" />
          Documentation Library
        </h1>
        <p className="text-gray-600 mt-1">
          Step-by-step guides for the iTrace integration. Use the search box to filter.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search: register farm, end cycle, generate QR..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          data-testid="docs-search"
        />
      </div>

      {/* Guide index */}
      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <BookOpenIcon className="h-12 w-12 mx-auto text-gray-300" />
          <p className="mt-2 text-gray-500">No guides match “{query}”.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, guides]) => {
          const Icon = CATEGORY_ICON[cat] || BookOpenIcon;
          return (
            <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Icon className="h-5 w-5 text-gray-500" />
                <h2 className="font-semibold text-gray-800">{cat}</h2>
                <span className="text-xs text-gray-500">({guides.length})</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {guides.map((g) => (
                  <li key={g.id}>
                    <button
                      onClick={() => setSelectedId(g.id)}
                      data-testid={`guide-${g.id}`}
                      className="w-full px-6 py-4 text-left hover:bg-gray-50 flex items-center justify-between gap-4 group"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 group-hover:text-blue-700">
                          {g.title}
                        </div>
                        <div className="text-sm text-gray-600 mt-0.5 truncate">{g.summary}</div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(g.tags || []).slice(0, 4).map((t) => (
                            <span
                              key={t}
                              className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Documentation;
